import React, { useCallback, useEffect, useMemo, useState } from "react";
import { axiosInstance } from "../../Api/AxiosConfig";
import { PresalesListTable } from "./PresalesListTable";
import { Waiting } from "../../Components/System/Waiting";
import { CarFilter, InitControlValueData } from "./CarFilter";
import { useGlobalDataContext } from "../../GlobalState/GlobalDataProvider";
import { StatusAndCheckoutButton } from "./StatusAndCheckoutButton";
import { findMinMaxDamageAmount, findMinMaxKm, findMinMaxPrices } from "../../Hooks/UseGenericFunctions";
import { filterAndSortList } from "../../Hooks/UseCarFilter";
import { CarViewEnum } from "../../Enums/CarViewEnum";
import { Showroom } from "../Showroom/Showroom";
import { useCarListParameters } from "../../Hooks/useCarListParameters";
import { useSearchParams } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import { registerUserActivity } from "../../Api/StatService";
import { TabButton } from "../../Components/System/Buttons/TabButton";
import { ListTypeEnum } from "../../Enums/ListTypeEnum";
import { PackageList } from "./PackageList";
import { UpdateDataPrompt } from "../../Components/System/Messages/UpdateDataPrompt";
import { useCarUpdateThrottle } from "../../Hooks/useCarUpdateThrottle";

export const Presales = ({ fnUpdateGlobalData, pushUpdate }) => {
    const [carzelleCars, setCarzelleCars] = useState();
    const [carzelleCarsMakes, setCarzelleCarsMakes] = useState();
    const [carzelleCarsFuels, setCarzelleCarsFuels] = useState();
    const [carzelleCarsTypes, setCarzelleCarsTypes] = useState();
    const [partnerCars, setPartnerCars] = useState();
    const [partnerCarsMakes, setPartnerCarsMakes] = useState();
    const [partnerCarsFuels, setPartnerCarsFuels] = useState();
    const [partnerCarsTypes, setPartnerCarsTypes] = useState();
    const [packagesCars, setPackagesCars] = useState();
    const [equipmentIcons, setEquipmentIcons] = useState();
    const [sortByField, setSortByField] = useState('designation');
    const [sortByFieldAscending, setSortByFieldAscending] = useState(true);
    const [filterData, setFilterData] = useState(InitControlValueData);
    const [updateCarList, setUpdateCarList] = useState(false);
    const [activeCar, setActiveCar] = useState();
    const [, setShowDetails] = useState(false);
    const { globalData } = useGlobalDataContext();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const isShowBasket = searchParams.get('showBasket') === 'false';
    const { listType, viewType, carHash, setCarListParameters } = useCarListParameters();
    const [opened, { open, close }] = useDisclosure(false);
    const [previousUpdateStatus, setPreviousUpdateStatus] = useState(false);
    const [showBasket, setShowBasket] = useState(true);
    const [termsAccepted, setTermsAccepted] = useState(false);

    // Use centralized car update throttling hook
    useCarUpdateThrottle(
        globalData?.carUpdateCounter,
        () => setUpdateCarList(prev => !prev),
        60000
    );

    const fetchCarList = (listTypeId = 1, setListDataVariable) => {
        axiosInstance
            .get('/shop/buy-list', {
                params: {
                    listTypeId: listTypeId,
                }
            })

            .then((response) => {
                fnUpdateGlobalData();
                if (listTypeId === 1) {
                    setCarzelleCarsMakes(response.data.makes);
                    setCarzelleCarsFuels(response.data.fuel);
                    setCarzelleCarsTypes(response.data.carTypes);
                }
                if (listTypeId === 2) {
                    setPartnerCarsMakes(response.data.makes);
                    setPartnerCarsFuels(response.data.fuel);
                    setPartnerCarsTypes(response.data.carTypes);
                }
                setEquipmentIcons(response.data.equipmentBase);
                setListDataVariable(response.data.list);
                registerUserActivity(3, 'Fetched Presales list');
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            });
    }

    const fetchCarPackageList = () => {
        axiosInstance
            .get('/shop/packages')

            .then((response) => {
                setPackagesCars(response.data);
                setEquipmentIcons(response.data.equipmentBase);
                fnUpdateGlobalData();
                registerUserActivity(3, 'Fetched Packages list');
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            });
    }

    useEffect(() => {
        let update = false;

        if (previousUpdateStatus !== updateCarList) {
            setPreviousUpdateStatus(updateCarList);
            update = true;
        }

        if ((!carzelleCars || update) && listType === ListTypeEnum.carzelle) {
            fetchCarList(1, setCarzelleCars);
        }

        if (((!partnerCars || update) && listType === ListTypeEnum.partner) || update) {
            fetchCarList(2, setPartnerCars);
        }

        if (((!packagesCars || update) && listType === ListTypeEnum.packages) || update) {
            fetchCarPackageList();
        }
    }, [updateCarList, pushUpdate, listType]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (carHash && carzelleCars) {
            const found = carzelleCars.find((car) => car.hash === carHash);
            if (found) {
                setActiveCar(found);
            }
        }
    }, [carzelleCars, carHash]);

    useEffect(() => {
        setShowDetails(true);
    }, [carHash]);

    const filteredAndSortedCars = useMemo(() => {
        if (listType === ListTypeEnum.carzelle) {
            return filterAndSortList(carzelleCars || [], filterData, sortByField, sortByFieldAscending);
        }
        if (listType === ListTypeEnum.partner) {
            return filterAndSortList(partnerCars || [], filterData, sortByField, sortByFieldAscending);
        }
    }, [carzelleCars, partnerCars, packagesCars, filterData, sortByField, sortByFieldAscending, listType]);

    const animateBasketIcon = () => {
        const element = document.getElementById('shopping_bag');  // Get the div element

        element.classList.add('animate-bounce');
        element.classList.remove('bg-purchase-color')
        element.classList.add('bg-sky-500');

        setTimeout(() => {
            element.classList.remove('bg-sky-500');
            element.classList.remove('animate-bounce');
            element.classList.add('bg-purchase-color');
        }, 1650);
    }

    useEffect(() => {
        setLoading(false);
    }, [listType]);

    const handleListTypeChange = (listInfo) => {
        setCarListParameters({ listType: listInfo.identifier });
        setCarListParameters({ viewType: listInfo.view });
        setFilterData(InitControlValueData);
    };

    const okToRenderList = useMemo(() => {
        if (listType === ListTypeEnum.carzelle && carzelleCars && carzelleCars.length > 0 && globalData && !loading) {
            return true;
        }
        if (listType === ListTypeEnum.partner && partnerCars && partnerCars.length > 0 && globalData && !loading) {
            return true;
        }
        if (listType === ListTypeEnum.packages && packagesCars && packagesCars.list.length > 0 && globalData && !loading) {
            return true;
        }
        return false;
    }, [listType, carzelleCars, partnerCars, packagesCars, globalData, loading]);

    if (okToRenderList) {
        const { minPrice, maxPrice } = findMinMaxPrices(listType === ListTypeEnum.carzelle ? carzelleCars : listType === ListTypeEnum.packages ? packagesCars.list[0].cars : partnerCars);
        const { minKm, maxKm } = findMinMaxKm(listType === ListTypeEnum.carzelle ? carzelleCars : listType === ListTypeEnum.packages ? packagesCars.list[0].cars : partnerCars);
        const { minDamageAmount, maxDamageAmount } = findMinMaxDamageAmount(listType === ListTypeEnum.carzelle ? carzelleCars : listType === ListTypeEnum.packages ? packagesCars.list[0].cars : partnerCars);

        return (
            <>
                <div className="lg:hidden flex items-center justify-center mt-12 p-8 text-2xl">
                    <div className="text-center">The presales function can only be used on desktop computers</div>
                </div>

                {/*<ScreenSizeIndicator/>*/}
                <div className="hidden lg:block">
                    <div className="mx-4 flex" style={{ height: '92vh' }}>
                        <div className="relative mr-1 2xl:mr-4 h-[92vh] w-[100vw]">
                            {showBasket && (
                                <div className="absolute top-[10px] right-4 z-20">
                                    <StatusAndCheckoutButton cars={globalData.basket.basket_lines} sum={globalData.basket.basket_totals.amount} currency={globalData.basket.basket_totals.currency} showBasket={isShowBasket} fnUpdate={() => setUpdateCarList(!updateCarList)} termsAccepted={termsAccepted} setTermsAccepted={setTermsAccepted} />
                                </div>
                            )}
                            <div className="bg-gray-300 rounded-lg p-4 h-[60px] flex items-center text-gray-500 mt-2">
                                <TabButton identifier={ListTypeEnum.carzelle} onActivate={() => setShowBasket(true)} onClick={handleListTypeChange} label="Confirmed & Fast Delivery" number={globalData.globals.salesListCount.carzelle} className="mr-4" active={listType === ListTypeEnum.carzelle} />
                                {globalData.globals.salesListCount.presales > 0 && <TabButton identifier={ListTypeEnum.partner} onActivate={() => setShowBasket(true)} onClick={handleListTypeChange} label="Partner Listings" className="mr-4" number={globalData.globals.salesListCount.presales} active={listType === ListTypeEnum.partner} />}
                                {globalData.globals.salesListCount.packages > 0 && <TabButton identifier={ListTypeEnum.packages} onActivate={() => setShowBasket(false)} onClick={handleListTypeChange} label="Packages" className="mr-4" number={globalData.globals.salesListCount.packages} active={listType === ListTypeEnum.packages} allowCardView={false} />}
                                {updateCarList && <div><UpdateDataPrompt /></div>}
                            </div>

                            {(viewType === CarViewEnum.list && listType === ListTypeEnum.carzelle) &&
                                <div className={`mt-2`}>
                                    <div className={`h-[92vh]`}>
                                        <PresalesListTable cars={filteredAndSortedCars} hidePrices={filterData.hide_prices} equipmentIcons={equipmentIcons} activeCarId={carHash} activeSortField={sortByField} activeSortAscending={sortByFieldAscending} fnSetSortByField={setSortByField} fnSetSortByFieldOrder={setSortByFieldAscending} fnUpdateCarList={() => {
                                            setUpdateCarList(!updateCarList);
                                            animateBasketIcon();
                                        }}
                                        />
                                    </div>
                                </div>
                            }

                            {(viewType === CarViewEnum.list && listType === ListTypeEnum.partner) &&
                                <div className={`mt-2`}>
                                    <div className={`h-[92vh]`}>
                                        <PresalesListTable cars={filteredAndSortedCars} hidePrices={filterData.hide_prices} equipmentIcons={equipmentIcons} activeCarId={carHash} activeSortField={sortByField} activeSortAscending={sortByFieldAscending} fnSetSortByField={setSortByField} fnSetSortByFieldOrder={setSortByFieldAscending} fnUpdateCarList={() => {
                                            setUpdateCarList(!updateCarList);
                                            animateBasketIcon();
                                        }}
                                        />
                                    </div>
                                </div>
                            }

                            {(viewType === CarViewEnum.list && listType === ListTypeEnum.packages) &&
                                <div className={`mt-2`}>
                                    <PackageList
                                        packages={packagesCars}
                                        equipmentIcons={equipmentIcons}
                                        carHash={carHash}
                                        fnUpdateCarList={() => {
                                            setUpdateCarList(!updateCarList);
                                        }}
                                    />
                                </div>
                            }

                            {viewType === CarViewEnum.cards && listType === ListTypeEnum.carzelle &&
                                <div>
                                    <Showroom selected={listType === ListTypeEnum.cards} selectedCar={activeCar} hidePrices={filterData.hide_prices} opened={opened} open={open} close={close} cars={filteredAndSortedCars} equipmentBaseIcons={equipmentIcons} fnUpdateData={() => setUpdateCarList(!updateCarList)} />
                                </div>
                            }

                            {viewType === CarViewEnum.cards && listType === ListTypeEnum.partner &&
                                <div>
                                    <Showroom selected={listType === ListTypeEnum.cards} selectedCar={activeCar} hidePrices={filterData.hide_prices} opened={opened} open={open} close={close} cars={filteredAndSortedCars} equipmentBaseIcons={equipmentIcons} fnUpdateData={() => setUpdateCarList(!updateCarList)} />
                                </div>
                            }
                        </div>
                        <div className="min-h-[100vh]">
                            <div className="pb-4 h-full min-w-[170px]">
                                {(carzelleCarsTypes && listType === ListTypeEnum.carzelle) && <CarFilter makes={carzelleCarsMakes} fuels={carzelleCarsFuels} carTypes={carzelleCarsTypes} filterData={filterData} fnFilterChange={(filterValues) => setFilterData(filterValues)} minPrice={minPrice} maxPrice={maxPrice} minKm={minKm} maxKm={maxKm} minDamageAmount={minDamageAmount} maxDamageAmount={maxDamageAmount} />}
                                {(partnerCarsTypes && listType === ListTypeEnum.partner) && <CarFilter makes={partnerCarsMakes} fuels={partnerCarsFuels} carTypes={partnerCarsTypes} filterData={filterData} fnFilterChange={(filterValues) => setFilterData(filterValues)} minPrice={minPrice} maxPrice={maxPrice} minKm={minKm} maxKm={maxKm} minDamageAmount={minDamageAmount} maxDamageAmount={maxDamageAmount} />}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )
    } else {
        return (
            <Waiting />
        );
    }
}